import Alpine from 'alpinejs'
import persist from '@alpinejs/persist'
import mask from '@alpinejs/mask'
import debounce from "lodash/debounce"
import { searchClient } from "@algolia/client-search"
import { TempusDominus, DateTime, Unit } from '@eonasdan/tempus-dominus'
import { ticketStorage, getDynamicTimes } from './helpers'
import { ticketsStruct, resultsStruct, locationStruct } from './structs'
import type { Ticket, SearchResult, AutoselectField, Errors, TripType } from './interfaces'

import './directives'
import registerTimePicker from './time-picker'

// @ts-ignore
Alpine.plugin([persist])
// @ts-ignore
Alpine.plugin(mask)

export class TicketBooking {
    readonly maxTickets: number = 5
    readonly minPax: number = 1
    readonly maxPax: number = 200
    client: ReturnType<typeof searchClient> | null = null
    errorsCount: number = 0
    debouncedInputSearch: ReturnType<typeof debounce>
    formNotice: string = ''
    index: number | null = null
    isSticky: boolean = false
    isSubmitting: boolean = false
    modalTrigger: HTMLElement | null = null
    styleContainer: CSSStyleDeclaration = {} as CSSStyleDeclaration
    styleOuterWrap: CSSStyleDeclaration = {} as CSSStyleDeclaration
    showFormNotice: boolean = false
    showModal: boolean = false
    showQuoteDetails: boolean = false
    showCart: boolean = false
    stickyThresholdEl: Element | null = null
    quoteSubmitted: boolean = false
    quoteFailureVisible: boolean = false
    quoteFailureMessage: string = ''
    tripType: TripType = 'one-way'
    isTouchDevice: boolean = window.matchMedia('(pointer: coarse)').matches
    success: string = 'Thank you for your message. We will get in touch with you shortly'
    typingIndex: number = 0
    tickets: Ticket[]
    formErrorsClass: string = 'field-invalid'
    constructor() {
        this.debouncedInputSearch = debounce((ticket, key, hitsPerPage, input) => this.inputSearch(ticket, key, hitsPerPage, input), 200)
        this.formNotice = ''
        // @ts-ignore
        // this.tickets = Alpine.$persist(ticketsStruct).using(ticketStorage) as unknown as Ticket[]
        this.tickets = ticketsStruct as unknown as Ticket[]

        this.typingIndex = 0
    }

    private processFlight(el: HTMLElement, isRoundTrip: boolean = false) {
        if (this.tickets.length >= this.maxTickets) {
            this.showTempMessage(`You can only book up to ${ this.maxTickets } flights`, 3000)
            return
        }
        const prevTicket = this.tickets[this.tickets.length - 1]

        if (isRoundTrip) {
            if (!prevTicket.origin.result.name || !prevTicket.destination.result.name) {
                if (!prevTicket.origin.result.name) {
                    this.highlightAndFocus('origin', prevTicket)
                } else {
                    this.highlightAndFocus('destination', prevTicket)
                }
                return
            }
        }

        const newId = this.getNextTicketId()
        const newTicket = isRoundTrip ?
            this.createFlightTicket(newId, prevTicket)
            : this.createFlightTicket(newId)

        this.tickets.push(newTicket)

        Alpine.nextTick(() => {
            const idx = `ticket-date-${ newId }`
            const dateInputEl: HTMLElement | null = document.getElementById(idx)
            this.initDatePicker?.(dateInputEl, prevTicket)
            this.initAutocomplete?.()
            this.setParentElHeight(el)
        })

    }
    addFlight(el: HTMLElement): void {
        this.tripType = 'multi'
        this.processFlight(el)
    }
    addRoundTrip(el: HTMLElement): void {
        this.setTripType('return', el)
    }
    blurSelect(ticket: Ticket, key: AutoselectField): void {
        setTimeout(() => {
            ticket[key].showResults = false
        }, 150)
    }
    book(): void {
        this.validateFields()
        if (this.countErrors() !== 0) {
            this.formNotice = "Please complete all fields to proceed."
            this.showFormNotice = true
            this.focusFirstInvalid('.quote-trip-panel')
        } else {
            this.quoteSubmitted = false
            this.quoteFailureVisible = false
            this.quoteFailureMessage = ''
            this.showQuoteDetails = true
            this.openDialog('.quote-form-body input:not([type="hidden"]), .quote-form-body select, .quote-form-body textarea, .quote-form-body button')
        }
    }
    openBookingModal(): void {
        this.tripType = 'one-way'
        this.tickets.splice(1)
        this.validateQuickQuote()

        if (this.countErrors() !== 0) {
            this.formNotice = "Please complete the route and date to proceed."
            this.showFormNotice = true
            this.focusFirstInvalid('.quick-quote-form')
            return
        }

        this.quoteSubmitted = false
        this.quoteFailureVisible = false
        this.quoteFailureMessage = ''
        this.showQuoteDetails = false
        this.showFormNotice = false
        this.openDialog()
    }
    clickAutocompleteInput(input: HTMLInputElement, ticket: Ticket, key: AutoselectField): void {
        this.showResults(ticket, key)
        this.positionAutocompleteResults(input)
    }
    focusAutocompleteInput(input: HTMLInputElement, ticket: Ticket, key: AutoselectField): void {
        if (this.isTouchDevice) {
            this.scrollAutocompleteIntoView(input)
        }

        this.clickAutocompleteInput(input, ticket, key)
    }
    closeModal(): void {
        this.isSubmitting = false
        this.showModal = false
        this.showQuoteDetails = false
        this.quoteSubmitted = false
        this.quoteFailureVisible = false
        this.quoteFailureMessage = ''
        document.body.style.overflow = ''
        const trigger = this.modalTrigger
        this.modalTrigger = null
        Alpine.nextTick(() => trigger?.focus())
        const successElement = document.getElementById('fluentform_1_success')
        if (successElement) {
            jQuery('.modal-scroll-box').addClass('overflow-y-auto')
            successElement.remove()
            this.ticketsReset()
        }
    }
    clearResult(ticket: Ticket, key: AutoselectField): void {
        ticket[key].query = ''
        ticket[key].result = {}
        ticket[key].results = structuredClone(resultsStruct)
        ticket.errors[key] = false
    }
    createFlightTicket(id: number, ticket?: Ticket): Ticket {
        return {
            id,
            origin: this.createSearchField(ticket?.destination),
            destination: this.createSearchField(ticket?.origin),
            errors: {
                origin: false,
                destination: false,
                date: false,
                time: false,
            },
            date: '',
            time: '',
            showTimeTable: false,
            pax: ticket?.pax ?? this.minPax,
            times: ticket?.times || getDynamicTimes(),
        }
    }
    createSearchField(source?: any): any {
        return {
            isSearching: false,
            query: source?.query || '',
            result: source?.result || {},
            results: JSON.parse(JSON.stringify(resultsStruct)),
            showResults: false,
            selectedIndex: 0,
        }
    }
    deleteLastTicket(): void {
        if (this.tickets.length == 1) return
        this.tickets.splice(this.tickets.length - 1, 1)
        if (this.tickets.length === 1) {
            this.tripType = 'one-way'
        }
    }
    deleteTicket(ticket: Ticket): void {
        const index = this.tickets.indexOf(ticket)
        if (!this.canRemoveTicket(ticket, index)) return
        this.tickets.splice(this.tickets.indexOf(ticket), 1)
    }
    formatSearchValues(values: string[]): string {
        return values.filter(value => value).join(' - ')
    }

    formClear(el: HTMLElement): void {
        this.ticketsReset()
        this.formNotice = ''
        this.showFormNotice = false
        this.typingIndex = 0
        Alpine.nextTick(() => {
            this.styleContainer.height = `${ el.clientHeight }px`
        })
    }
    getSeparatorClass(index: number): { separator: boolean } {
        return {
            separator: index >= 0 && index < this.tickets.length - 2
        }
    }
    getLegLabel(ticket: Ticket, index: number): string {
        if (this.tripType === 'return' && index === 1) {
            return 'Return'
        }

        return `Leg ${ index + 1 }`
    }
    canRemoveTicket(ticket: Ticket, index: number): boolean {
        return this.tripType === 'multi' && index > 0 && this.tickets.length > 1
    }
    isTripType(type: TripType): boolean {
        return this.tripType === type
    }
    onTripTabKeydown(event: KeyboardEvent, el?: HTMLElement): void {
        const types: TripType[] = ['one-way', 'return', 'multi']
        const current = types.indexOf(this.tripType)
        let next = current

        if (event.key === 'ArrowRight') next = (current + 1) % types.length
        else if (event.key === 'ArrowLeft') next = (current - 1 + types.length) % types.length
        else if (event.key === 'Home') next = 0
        else if (event.key === 'End') next = types.length - 1
        else return

        event.preventDefault()
        this.setTripType(types[next], el)
        Alpine.nextTick(() => {
            document.querySelector<HTMLElement>(`[data-trip-type="${ types[next] }"]`)?.focus()
        })
    }
    setTripType(type: TripType, el?: HTMLElement): void {
        const previousTripType = this.tripType
        this.tripType = type
        this.showFormNotice = false
        this.normalizeTripType(previousTripType)

        Alpine.nextTick(() => {
            this.initializeDateInputs(el ?? document)
            if (el) {
                this.setParentElHeight(el)
            }
        })
    }
    normalizeTripType(previousTripType: TripType = this.tripType): void {
        if (!this.tickets.length) {
            this.tickets.push(this.createFlightTicket(0))
        }

        if (this.tripType === 'one-way') {
            this.tickets.splice(1)
            return
        }

        if (this.tripType === 'multi' && previousTripType !== 'multi') {
            this.tickets.splice(1)
            return
        }

        if (this.tripType === 'return') {
            this.tickets.splice(2)

            if (this.tickets.length === 1) {
                this.tickets.push(this.createFlightTicket(this.getNextTicketId(), this.tickets[0]))
            } else {
                const returnLeg = this.createFlightTicket(this.tickets[1].id, this.tickets[0])
                returnLeg.date = this.tickets[1].date
                returnLeg.time = this.tickets[1].time
                returnLeg.errors = this.tickets[1].errors
                returnLeg.showTimeTable = this.tickets[1].showTimeTable
                returnLeg.times = this.tickets[1].times
                this.tickets[1] = returnLeg
            }
        }
    }
    getNextTicketId(): number {
        return Math.max(-1, ...this.tickets.map(ticket => ticket.id)) + 1
    }

    highlightAndFocus(field: keyof Errors, ticket: Ticket): void {
        const input = Array.from(document.querySelectorAll<HTMLElement>(
            `[id$="ticket-${ field }-${ ticket.id }"]`
        )).find(element => element.getClientRects().length > 0)

        this.showTempMessage(`Select ${ field } to continue`, 3000)

        ticket.errors[field] = true

        if (input) {
            input.focus()
        }
    }

    private focusFirstInvalid(scopeSelector: string): void {
        Alpine.nextTick(() => {
            const invalid = Array.from(document.querySelectorAll<HTMLElement>(`${ scopeSelector } [aria-invalid="true"]`))
                .find(element => element.getClientRects().length > 0)
            invalid?.focus()
        })
    }

    isEmpty() {
        return this.tickets.length === 0
    }
    isMoreThanOne() {
        return this.tickets.length > 1
    }
    initializeDateInputs(scope: ParentNode = document) {
        if (this.isTouchDevice) return
        Alpine.nextTick(() => {
            scope.querySelectorAll<HTMLElement>('.date-input').forEach(el => {
                if (el.dataset.datePickerBound === 'true') return
                const initOnFocus = () => {
                    this.initTempusDominus(el)
                    el.removeEventListener('focus', initOnFocus)
                    el.removeEventListener('click', initOnFocus)
                }
                el.dataset.datePickerBound = 'true'
                el.addEventListener('focus', initOnFocus)
                el.addEventListener('click', initOnFocus)
            })
        })
    }
    initialize(outerWrapEl: HTMLElement) {
        const content = outerWrapEl.closest('.entry-content, .wp-block-post-content')
        this.stickyThresholdEl = content?.querySelector(':scope > .wp-block-wpvb-container, :scope > section')
            ?? content?.firstElementChild
            ?? null
        this.stick(outerWrapEl)
        this.initializeDateInputs()
    }

    initTempusDominus(el: HTMLElement | null, minDate: DateTime = new DateTime()): void {
        if (el) {
            new TempusDominus(el, {
                display: {
                    icons: {
                        type: 'icons',
                        time: 'fa-solid fa-clock',
                        date: 'fa-solid fa-calendar',
                        up: 'fa-solid fa-arrow-up',
                        down: 'fa-solid fa-arrow-down',
                        previous: 'svg-arrow prev',
                        next: 'svg-arrow next',
                        today: 'fa-solid fa-calendar-check',
                        clear: 'fa-solid fa-trash',
                        close: 'fa-solid fa-xmark'
                    },
                    viewMode: 'calendar',
                    components: {
                        minutes: false,
                        hours: false,
                        seconds: false,
                    },
                },
                restrictions: {
                    minDate: minDate,
                },
                localization: {
                    locale: 'en-GB',
                    format: 'dd/MM/yyyy',
                },
            })
        }
    }
    initDatePicker(dateInputEl: HTMLElement | null, prevTicket?: Ticket): void {
        let date = new DateTime()
        if (prevTicket) {
            const prevDate = prevTicket.date || ''
            if (prevDate) {
                const [day, month, year] = prevDate.split('/')

                const parsedDate = DateTime.fromString(`${ day }/${ month }/${ year }`, {
                    format: 'dd/MM/yyyy',
                })

                if (DateTime.isValid(parsedDate)) {
                    date = parsedDate.manipulate(0, Unit.date)
                } else {
                    console.error("Invalid DateTime format.")
                }
            }
        }
        this.initTempusDominus(dateInputEl, date)
    }
    initAutocomplete(): void {
        this.client = searchClient(
            (window as any).algoliaAppId,
            (window as any).algoliaSearchOnlyApiKey
        )
    }
    onAutocompleteInput(ticket: Ticket, key: AutoselectField, hitsPerPage: number, input: HTMLInputElement): void {
        ticket[key].result = {}
        ticket.errors[key] = false
        this.debouncedInputSearch(ticket, key, hitsPerPage, input)
    }
    async inputSearch(ticket: Ticket, key: AutoselectField, hitsPerPage: number, input?: HTMLInputElement): Promise<void> {
        this.initAutocomplete()
        const query = ticket[key].query
        const resultLimit = Math.min(Number(hitsPerPage), 6)

        if (!query.trim()) {
            ticket[key].results.hits = []
            return
        }

        ticket[key].isSearching = true

        try {
            const results = await this.client?.searchSingleIndex({
                indexName: "airports",
                searchParams: {
                    query,
                    hitsPerPage: resultLimit,
                }
            })

            if (ticket[key].query !== query || ticket[key].result.name) return

            if (results?.hits.length) {
                ticket[key].results.hits = results.hits.slice(0, resultLimit)
            }
            ticket[key].selectedIndex = 0
            ticket[key].showResults = true
            this.positionAutocompleteResults(input ?? document.getElementById(`ticket-${ key }-${ ticket.id }`) as HTMLInputElement)
        } catch (error) {
            console.error("Error fetching search results:", error)
        } finally {
            ticket[key].isSearching = false
        }

    }
    onAutocompleteKeydown(event: KeyboardEvent, resultsEl: HTMLElement | undefined, ticket: Ticket, key: AutoselectField): void {
        const hits = ticket[key].results.hits

        if (event.key === 'Escape') {
            if (ticket[key].showResults) {
                event.preventDefault()
                event.stopPropagation()
                ticket[key].showResults = false
            }
            return
        }

        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault()
            this.showResults(ticket, key)
        }

        if (event.key === "ArrowDown" && ticket[key].selectedIndex < hits.length - 1) {
            ticket[key].selectedIndex++
            if (resultsEl) this.scroll(true, resultsEl)
        }

        if (event.key === "ArrowUp" && ticket[key].selectedIndex > 0) {
            ticket[key].selectedIndex--
            if (resultsEl) this.scroll(false, resultsEl)
        }

        if (event.key === "Enter" && ticket[key].showResults && hits.length) {
            event.preventDefault()
            const result: SearchResult = hits[ticket[key].selectedIndex]

            ticket[key].query = result.name
            ticket[key].result = result
            ticket[key].showResults = false
            ticket.errors[key] = false
            if (this.countErrors() === 0) {
                this.showFormNotice = false
            }
        }
    }
    scroll(down: boolean = true, resultsEl: HTMLElement): void {
        const length = 54 * 0.75
        resultsEl.scrollBy({ top: down ? length : -length, behavior: "smooth" })
    }
    showTempMessage(msg: string, delay: number = 3000): void {
        this.formNotice = msg
        this.showFormNotice = true
        setTimeout(() => {
            this.formNotice = ''
            this.showFormNotice = false
        }, delay)
    }
    selectAutocompleteResult(ticket: Ticket, result: SearchResult, index: number, key: AutoselectField): void {
        ticket[key].query = result.name
        ticket[key].result = result
        ticket[key].results = structuredClone(resultsStruct)
        ticket[key].showResults = false
        ticket[key].selectedIndex = index
        ticket.errors[key] = false
        if (this.countErrors() === 0) {
            this.showFormNotice = false
        }
    }
    selectDate(e: Event, ticket: Ticket): void {
        const target = e.target as HTMLInputElement
        ticket.date = target.value
        ticket.errors.date = false
        if (this.countErrors() === 0) {
            this.showFormNotice = false
        }
        this.updateTimes(ticket)
    }
    normalizePax(value: number | string): number {
        const parsed = Number.parseInt(String(value), 10)
        if (!Number.isFinite(parsed)) return this.minPax
        return Math.min(this.maxPax, Math.max(this.minPax, parsed))
    }
    updatePax(ticket: Ticket, event: Event): void {
        const input = event.target as HTMLInputElement
        const rawValue = input.value.trim()
        if (rawValue === '') {
            ticket.pax = ''
            return
        }
        if (!/^\d+$/.test(rawValue)) {
            input.value = String(ticket.pax || this.minPax)
            return
        }
        const pax = this.normalizePax(rawValue)
        ticket.pax = pax
        input.value = String(pax)
    }
    commitPax(ticket: Ticket, event: Event): void {
        const input = event.target as HTMLInputElement
        const pax = this.normalizePax(ticket.pax)
        ticket.pax = pax
        input.value = String(pax)
    }
    canDecreasePax(ticket: Ticket): boolean {
        return this.normalizePax(ticket.pax) > this.minPax
    }
    canIncreasePax(ticket: Ticket): boolean {
        return this.normalizePax(ticket.pax) < this.maxPax
    }
    decreasePax(ticket: Ticket): void {
        ticket.pax = Math.max(this.minPax, this.normalizePax(ticket.pax) - 1)
    }
    increasePax(ticket: Ticket): void {
        ticket.pax = Math.min(this.maxPax, this.normalizePax(ticket.pax) + 1)
    }
    onNativeDateChange(ticket: Ticket, value: string): void {
        if (!value) return

        const selected = new Date(value)
        const today = new Date()
        today.setHours(0, 0, 0, 0)

        if (selected < today) {
            this.showTempMessage('Please select today or a future date', 3000)
            ticket.errors.date = true
            return
        }

        const [y, m, d] = value.split('-')
        ticket.date = `${d}/${m}/${y}`
        ticket.errors.date = false
        if (this.countErrors() === 0) {
            this.showFormNotice = false
        }
        this.updateTimes(ticket)
    }
    dateToNative(date: string): string {
        if (!date) return ''
        const [d, m, y] = date.split('/')
        return `${y}-${m}-${d}`
    }
    showResults(ticket: Ticket, key: AutoselectField): void {
        if (ticket[key].results.hits.length) {
            ticket[key].showResults = true
        }
    }
    positionAutocompleteResults(input: HTMLInputElement | null): void {
        if (!this.isTouchDevice || !input) return

        Alpine.nextTick(() => {
            requestAnimationFrame(() => {
            const results = input.closest('.autocomplete-wrap')?.querySelector<HTMLElement>('.results')
                if (!results) return

                const viewport = window.visualViewport
                const viewportTop = viewport?.offsetTop ?? 0
                const inputBounds = input.getBoundingClientRect()
                const spaceAbove = inputBounds.top - viewportTop - 8
                const placeAbove = true
                const availableHeight = Math.max(120, spaceAbove)

                results.classList.toggle('results-above', placeAbove)
                results.style.setProperty('--results-available-height', `${ availableHeight }px`)
                results.scrollTop = placeAbove ? results.scrollHeight : 0
            })
        })
    }
    scrollAutocompleteIntoView(input: HTMLInputElement): void {
        const scroll = () => {
            const viewport = window.visualViewport
            const viewportTop = viewport?.offsetTop ?? 0
            const viewportHeight = viewport?.height ?? window.innerHeight
            const scrollBox = input.closest<HTMLElement>('.modal-scroll-box')
            const inputBounds = input.getBoundingClientRect()
            const targetTop = viewportTop + Math.max(120, Math.min(viewportHeight - 60, viewportHeight * 0.7))

            if (scrollBox) {
                const scrollBoxBounds = scrollBox.getBoundingClientRect()
                const inputTop = inputBounds.top - scrollBoxBounds.top + scrollBox.scrollTop
                scrollBox.scrollTo({ top: Math.max(0, inputTop - targetTop) })
            } else {
                window.scrollTo({ top: Math.max(0, window.scrollY + inputBounds.top - targetTop) })
            }
        }

        requestAnimationFrame(scroll)
        window.visualViewport?.addEventListener('resize', scroll, { once: true })
        setTimeout(() => {
            scroll()
            this.positionAutocompleteResults(input)
        }, 250)
    }
    prepareSubmission(form?: HTMLFormElement | null): void {
        if (this.isSubmitting) return

        const targetForm = form
            || (document.querySelector('.modal.quote-form.shown #fluentform_1') as HTMLFormElement | null)
            || (document.querySelector('#fluentform_1') as HTMLFormElement | null)

        if (targetForm) {
            const hiddenField = targetForm.querySelector('input[name="tickets"]') as HTMLInputElement | null
            this.isSubmitting = true
            this.quoteFailureVisible = false
            this.quoteFailureMessage = ''

            if (hiddenField) {
                hiddenField.value = JSON.stringify(this.tickets)
            }

            let modalMarker = targetForm.querySelector('input[name="avianese_modal_submission"]') as HTMLInputElement | null
            if (targetForm.closest('.modal.quote-form')) {
                if (!modalMarker) {
                    modalMarker = document.createElement('input')
                    modalMarker.type = 'hidden'
                    modalMarker.name = 'avianese_modal_submission'
                    targetForm.appendChild(modalMarker)
                }
                modalMarker.value = '1'
            } else if (modalMarker) {
                modalMarker.remove()
            }
        }
    }
    submit(): void {
        if (this.isSubmitting) return

        const form = (document.querySelector('.modal.quote-form.shown #fluentform_1') as HTMLFormElement | null)
            || (document.querySelector('#fluentform_1') as HTMLFormElement | null)
        if (form) {
            const hiddenField = form.querySelector('input[name="tickets"]') as HTMLInputElement | null

            this.isSubmitting = true

            if (hiddenField) {
                hiddenField.value = JSON.stringify(this.tickets)
            }

            jQuery(form).trigger('submit')

            jQuery(form).on('fluentform_submission_failed', () => {
                this.isSubmitting = false
            })
        }
    }
    completeSubmission(): void {
        this.isSubmitting = false
        this.quoteSubmitted = true
        this.quoteFailureVisible = false
        this.quoteFailureMessage = ''
        this.showQuoteDetails = true
        this.openDialog('.quote-form-close')
    }
    failSubmission(message: string = 'We could not send your request. Please use one of the direct contact options below.'): void {
        this.isSubmitting = false
        this.quoteSubmitted = false
        this.quoteFailureMessage = message
        this.quoteFailureVisible = true
        this.showQuoteDetails = true
        this.openDialog('.quote-form-failure a, .quote-form-close')
    }

    setParentElHeight(el: HTMLElement): void {
        const parent = el.parentElement

        if (parent) {
            this.styleContainer.height = `${ el.clientHeight }px`
        }
    }

    applySticky(el: HTMLElement) {
        const thresholdEl = this.stickyThresholdEl ?? document.querySelector('.booking-form-container')

        if (thresholdEl) {
            const thresholdRect = thresholdEl.getBoundingClientRect()

            if (thresholdRect.bottom <= 0) {
                this.isSticky = true
                if (window.innerWidth < 768) {
                    this.styleOuterWrap.paddingBottom = `120px`
                }
            } else {
                this.isSticky = false
                this.showCart = false
                if (window.innerWidth < 768) {
                    this.styleOuterWrap.height = ``
                    this.styleOuterWrap.paddingBottom = `0`
                }
            }
            this.styleContainer.height = `${ el.children[0].clientHeight }px`

        }
    }
    stick(el: HTMLElement): void {
        Alpine.nextTick(() => {
            this.applySticky(el)
        })

        const handleScroll = () => this.applySticky(el)
        const handleResize = () => this.applySticky(el)
        window.addEventListener('scroll', handleScroll)
        window.addEventListener('resize', handleResize)
    }

    ticketsReset() {
        this.tripType = 'one-way'
        this.tickets.splice(1, this.tickets.length - 1)
        this.tickets[0].destination = structuredClone(locationStruct)
        this.tickets[0].origin = structuredClone(locationStruct)
        this.tickets[0].errors = {
            origin: false,
            destination: false,
            date: false,
            time: false,
        }
        this.tickets[0].pax = 1
        this.tickets[0].date = ''
        this.tickets[0].time = ''
        this.tickets[0].times = getDynamicTimes()
    }
    toggleCart(): void {
        this.showCart = !this.showCart
    }
    openBookingForm(): void {
        this.showCart = false
        this.showQuoteDetails = false
        this.quoteSubmitted = false
        this.quoteFailureVisible = false
        this.quoteFailureMessage = ''
        this.showFormNotice = false
        this.openDialog()
    }
    private openDialog(focusSelector: string = '.quote-form-close'): void {
        if (!this.showModal && document.activeElement instanceof HTMLElement) {
            this.modalTrigger = document.activeElement
        }

        this.showModal = true
        document.body.style.overflow = 'hidden'
        Alpine.nextTick(() => {
            requestAnimationFrame(() => {
                document.querySelector<HTMLElement>(`.modal.quote-form.shown ${ focusSelector }`)?.focus()
            })
        })
    }
    trapModalFocus(event: KeyboardEvent, modal: HTMLElement): void {
        const focusable = Array.from(modal.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )).filter(element => element.getClientRects().length > 0)

        if (!focusable.length) {
            event.preventDefault()
            return
        }

        const first = focusable[0]
        const last = focusable[focusable.length - 1]
        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault()
            last.focus()
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault()
            first.focus()
        }
    }
    updateTimes(ticket: Ticket) {
        ticket.times = getDynamicTimes()
    }
    countErrors() {
        let errorsCount = 0
        this.tickets.forEach(ticket => {
            errorsCount += Object.values(ticket.errors).filter(Boolean).length
        })
        return errorsCount
    }
    validateFields(): void {
        const fields: (keyof Errors)[] = ['origin', 'destination', 'date']
        for (const ticket of this.tickets) {
            for (const field of fields) {

                const isEmpty = field === 'origin' || field === 'destination'
                    ? !(ticket[field].result && Object.keys(ticket[field].result).length)
                    : !ticket[field]

                if (isEmpty) {
                    ticket.errors[field] = true
                }
            }
        }
    }
    validateQuickQuote(): void {
        const ticket = this.tickets[0]
        const fields: (keyof Errors)[] = ['origin', 'destination', 'date']

        ticket.errors.time = false
        for (const field of fields) {
            const isEmpty = field === 'origin' || field === 'destination'
                ? !(ticket[field].result && Object.keys(ticket[field].result).length)
                : !ticket[field]

            ticket.errors[field] = isEmpty
        }
    }
    static createObject(): any {
        const instance = new TicketBooking()
        return new Proxy(instance, {
            get(target, prop, receiver) {
                return Reflect.get(target, prop, receiver)
            },
            set(target, prop, value, receiver) {
                return Reflect.set(target, prop, value, receiver)
            }
        })
    }
}
export default (): void => {
    registerTimePicker()
    Alpine.store('data', TicketBooking.createObject())
}
